import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Save, Loader2, AlertTriangle, User, Eye, CheckCircle, Bell, Upload, Mail, Brain, Camera, BarChart3, Calendar, Clock, Phone, Video, XCircle, Settings, CreditCard, Link2, Unlink, Crown, Target, X, MapPin, DollarSign, BadgeCheck, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { ProfileAnalyticsDashboard } from '../components/AnalyticsDashboard';
import ProfessionalAssistant from '../components/ProfessionalAssistant';
import { AIDescriptionButton } from '../components/AIDescriptionButton';
import QuoteRequestsInbox from '../components/QuoteRequestsInbox';
import authAxios, { API } from '../utils/authAxios';
import { CHILEAN_REGIONS, getCitiesByRegion } from '../constants/chileanLocations';

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt', 'Viña del Mar', 'Iquique', 'Calama', 'Copiapó', 'Coquimbo', 'Chillán', 'Osorno', 'Valdivia', 'Punta Arenas'];

const professionalTypes = [
  { value: 'Psicólogo/a', label: 'Psicólogo/a' },
  { value: 'Psiquiatra', label: 'Psiquiatra' },
  { value: 'Terapeuta', label: 'Terapeuta' },
  { value: 'Consejero/a', label: 'Consejero/a' },
  { value: 'Trabajador/a Social', label: 'Trabajador/a Social' },
  { value: 'Coach', label: 'Coach' },
  { value: 'Médico', label: 'Médico' },
  { value: 'Enfermero/a', label: 'Enfermero/a' },
  { value: 'Otro', label: 'Otro' }
];

const serviceOptions = [
  'Terapia Individual', 'Terapia de Pareja', 'Terapia Familiar', 'Terapia Grupal',
  'Evaluación Psicológica', 'Intervención en Crisis', 'Orientación Vocacional',
  'Tratamiento de Adicciones', 'Tratamiento de Ansiedad', 'Tratamiento de Depresión',
  'Trastornos Alimenticios', 'TDAH', 'Autismo', 'Trauma y TEPT'
];

const approachOptions = [
  'Cognitivo-Conductual', 'Cognitivo', 'Conductual', 'Psicoanalítico', 'Humanista', 'Sistémico',
  'Gestalt', 'EMDR', 'Mindfulness', 'ACT', 'DBT', 'Integrativo', 'Psicoeducativo', 'Narrativo'
];

const modalityOptions = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'online', label: 'Online' },
  { value: 'domicilio', label: 'A domicilio' }
];

// Preview Component - Shows how the professional will look in the public directory
function ProfessionalPreview({ data }) {
  if (!data || !data.name) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-12 text-center">
          <Eye className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">Completa los datos para ver la vista previa</p>
        </CardContent>
      </Card>
    );
  }

  var proImage = data.profile_image || data.picture;
  var modalityList = Array.isArray(data.modality) ? data.modality : (data.modality ? [data.modality] : []);

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-slate-50 flex items-center gap-2">
          <Eye className="w-5 h-5 text-emerald-400" />
          Vista Previa del Perfil
        </CardTitle>
        <p className="text-slate-400 text-sm">Así se verá tu perfil en el directorio público</p>
      </CardHeader>
      <CardContent>
        {/* Preview Card */}
        <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-md mx-auto p-5">
          {/* Premium Badge */}
          {data.is_premium && (
            <div className="flex items-center gap-1 text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-medium mb-3 w-fit">
              <Crown className="w-3 h-3" />
              <span>Premium</span>
            </div>
          )}
          
          <div className="flex items-center gap-4 mb-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
              {proImage ? (
                <img src={proImage} alt={data.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-slate-800 truncate">
                {data.name}
              </h3>
              <p className="text-emerald-600 font-medium text-sm">
                {data.professional_type || 'Profesional'}
              </p>
              {data.city && (
                <p className="text-slate-500 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" /> {data.city}
                </p>
              )}
            </div>
          </div>
          
          {/* Modality */}
          {modalityList.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {modalityList.map(function(mod, idx) {
                return (
                  <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                    {mod === 'presencial' ? 'Presencial' : mod === 'online' ? 'Online' : 'A domicilio'}
                  </span>
                );
              })}
            </div>
          )}
          
          {/* Services */}
          {data.services && data.services.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {data.services.slice(0, 3).map(function(service, idx) {
                return (
                  <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {service}
                  </span>
                );
              })}
              {data.services.length > 3 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  +{data.services.length - 3} más
                </span>
              )}
            </div>
          )}
          
          {/* Prices */}
          {(data.consultation_price || data.session_price) && (
            <div className="flex items-center gap-4 text-sm text-slate-600 border-t border-slate-100 pt-3">
              {data.consultation_price && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Primera consulta: ${Number(data.consultation_price).toLocaleString('es-CL')}
                </span>
              )}
              {data.session_price && !data.consultation_price && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Sesión: ${Number(data.session_price).toLocaleString('es-CL')}
                </span>
              )}
            </div>
          )}
          
          {/* Schedule */}
          {data.schedule && (
            <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
              <Clock className="w-4 h-4" />
              <span>{data.schedule}</span>
            </div>
          )}
          
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mt-3">
            {data.is_verified && (
              <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium">
                <BadgeCheck className="w-3 h-3" /> Verificado
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProfessionalDashboard() {
  const auth = useAuth();
  const [profile, setProfile] = useState(null);
  const [patients, setPatients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [appointmentStats, setAppointmentStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const fileInputRef = useRef(null);
  const [analytics, setAnalytics] = useState(null);
  const [pricingData, setPricingData] = useState({ reservation_price: 15000, consultation_price: null, accepts_online_payment: false });
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(function() {
    async function load() {
      try {
        const results = await Promise.all([
          authAxios.get(API + '/professional/profile', { withCredentials: true }),
          authAxios.get(API + '/professional/patients', { withCredentials: true }).catch(function() { return { data: [] }; }),
          authAxios.get(API + '/professional/alerts', { withCredentials: true }).catch(function() { return { data: [] }; }),
          authAxios.get(API + '/professional/messages', { withCredentials: true }).catch(function() { return { data: { messages: [] } }; }),
          authAxios.get(API + '/professional/analytics', { withCredentials: true }).catch(function() { return { data: null }; }),
          authAxios.get(API + '/professional/pricing', { withCredentials: true }).catch(function() { return { data: { reservation_price: 15000, consultation_price: null, accepts_online_payment: false } }; }),
          authAxios.get(API + '/professional/google-calendar/status', { withCredentials: true }).catch(function() { return { data: { connected: false } }; })
        ]);
        setProfile(results[0].data);
        setFormData(results[0].data);
        setPatients(results[1].data || []);
        setAlerts(results[2].data || []);
        setMessages(results[3].data.messages || []);
        setAnalytics(results[4].data);
        setPricingData(results[5].data);
        setCalendarConnected(results[6].data.connected);
        
        // Load appointments
        if (results[0].data && results[0].data.user_id) {
          try {
            const aptRes = await authAxios.get(API + '/appointments/professional/' + results[0].data.user_id, { withCredentials: true });
            setAppointments(aptRes.data.appointments || []);
            setAppointmentStats(aptRes.data.stats || { total: 0, pending: 0, confirmed: 0, completed: 0 });
          } catch (e) {
            console.error('Error loading appointments:', e);
          }
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
    
    // Check URL params for calendar connection status
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === '1') {
      toast.success('Google Calendar conectado exitosamente');
      setCalendarConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar_error') === '1') {
      toast.error('Error al conectar Google Calendar');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function updateAppointmentStatus(appointmentId, newStatus) {
    try {
      await authAxios.put(API + '/appointments/' + appointmentId, { status: newStatus }, { withCredentials: true });
      toast.success('Cita actualizada');
      // Reload appointments
      if (profile && profile.user_id) {
        const aptRes = await authAxios.get(API + '/appointments/professional/' + profile.user_id, { withCredentials: true });
        setAppointments(aptRes.data.appointments || []);
        setAppointmentStats(aptRes.data.stats || { total: 0, pending: 0, confirmed: 0, completed: 0 });
      }
    } catch (e) {
      toast.error('Error al actualizar cita');
    }
  }

  function handleChange(e) {
    setFormData(function(prev) { return Object.assign({}, prev, { [e.target.name]: e.target.value }); });
  }

  function handleMultiSelect(field, value) {
    setFormData(function(prev) {
      var current = prev[field] || [];
      var newArr = current.includes(value) 
        ? current.filter(function(v) { return v !== value; })
        : current.concat([value]);
      return Object.assign({}, prev, { [field]: newArr });
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await authAxios.put(API + '/professional/profile', formData, { withCredentials: true });
      toast.success('Perfil actualizado');
    } catch (e) {
      toast.error('Error al actualizar');
    }
    setSaving(false);
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Client-side validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('La imagen es muy grande. Máximo 10MB');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await authAxios.post(API + '/upload/image', formDataUpload, { withCredentials: true });
      const imageUrl = res.data.url;
      setFormData(function(prev) { return Object.assign({}, prev, { profile_image: imageUrl }); });
      toast.success('Foto subida correctamente');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al subir foto. Intente con otra imagen.';
      toast.error(errorMsg);
      console.error('Upload error:', err);
    }
    setUploading(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-50 mb-2" data-testid="professional-dashboard-title">Panel del Profesional</h1>
              <p className="text-slate-400">Bienvenido, {auth.user?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              {profile?.is_public ? (
                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm"><Eye className="w-4 h-4" />Visible</span>
              ) : (
                <span className="flex items-center gap-1 px-3 py-1 bg-yellow-500/10 text-yellow-400 rounded-full text-sm">Oculto</span>
              )}
              {profile?.is_verified && (
                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm"><CheckCircle className="w-4 h-4" />Verificado</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-800 pb-4">
            <Button variant={activeTab === 'profile' ? 'default' : 'ghost'} onClick={function() { setActiveTab('profile'); }} className={activeTab === 'profile' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><User className="w-4 h-4 mr-2" />Mi Perfil</Button>
            <Button variant={activeTab === 'appointments' ? 'default' : 'ghost'} onClick={function() { setActiveTab('appointments'); }} className={activeTab === 'appointments' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Calendar className="w-4 h-4 mr-2" />Citas {appointmentStats.pending > 0 && <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">{appointmentStats.pending}</span>}</Button>
            <Button variant={activeTab === 'analytics' ? 'default' : 'ghost'} onClick={function() { setActiveTab('analytics'); }} className={activeTab === 'analytics' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><BarChart3 className="w-4 h-4 mr-2" />Estadísticas</Button>
            <Button variant={activeTab === 'photo' ? 'default' : 'ghost'} onClick={function() { setActiveTab('photo'); }} className={activeTab === 'photo' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Camera className="w-4 h-4 mr-2" />Foto</Button>
            <Button variant={activeTab === 'patients' ? 'default' : 'ghost'} onClick={function() { setActiveTab('patients'); }} className={activeTab === 'patients' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Users className="w-4 h-4 mr-2" />Pacientes {patients.length > 0 && <span className="ml-2 px-2 py-0.5 bg-white/20 text-white text-xs rounded-full">{patients.length}</span>}</Button>
            <Button variant={activeTab === 'messages' ? 'default' : 'ghost'} onClick={function() { setActiveTab('messages'); }} className={activeTab === 'messages' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Mail className="w-4 h-4 mr-2" />Mensajes {messages.length > 0 && <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{messages.length}</span>}</Button>
            <Button variant={activeTab === 'quotes' ? 'default' : 'ghost'} onClick={function() { setActiveTab('quotes'); }} className={activeTab === 'quotes' ? 'bg-blue-500' : 'text-blue-400 hover:text-blue-300'} data-testid="pro-quotes-tab"><Inbox className="w-4 h-4 mr-2" />Cotizaciones</Button>
            <Button variant={activeTab === 'alerts' ? 'default' : 'ghost'} onClick={function() { setActiveTab('alerts'); }} className={activeTab === 'alerts' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Bell className="w-4 h-4 mr-2" />Alertas {alerts.length > 0 && <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{alerts.length}</span>}</Button>
            <Button variant={activeTab === 'advertising' ? 'default' : 'ghost'} onClick={function() { setActiveTab('advertising'); }} className={activeTab === 'advertising' ? 'bg-amber-500' : 'text-amber-400 hover:text-amber-300'}><Crown className="w-4 h-4 mr-2" />Publicidad</Button>
            <Button variant={activeTab === 'settings' ? 'default' : 'ghost'} onClick={function() { setActiveTab('settings'); }} className={activeTab === 'settings' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Settings className="w-4 h-4 mr-2" />Configuración</Button>
          </div>

          {activeTab === 'appointments' && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center"><Calendar className="w-5 h-5 text-slate-400" /></div>
                      <div><p className="text-2xl font-bold text-slate-50">{appointmentStats.total}</p><p className="text-xs text-slate-500">Total</p></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-yellow-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-400" /></div>
                      <div><p className="text-2xl font-bold text-yellow-400">{appointmentStats.pending}</p><p className="text-xs text-slate-500">Pendientes</p></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-blue-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-blue-400" /></div>
                      <div><p className="text-2xl font-bold text-blue-400">{appointmentStats.confirmed}</p><p className="text-xs text-slate-500">Confirmadas</p></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-emerald-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-400" /></div>
                      <div><p className="text-2xl font-bold text-emerald-400">{appointmentStats.completed}</p><p className="text-xs text-slate-500">Completadas</p></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Appointments List */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" />Mis Citas</CardTitle></CardHeader>
                <CardContent>
                  {appointments.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No tienes citas programadas</p>
                  ) : (
                    <div className="space-y-4">
                      {appointments.map(function(apt) {
                        var statusColors = {
                          pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                          confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                          completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                          cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
                          no_show: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                        };
                        var statusLabels = { pending: 'Pendiente', confirmed: 'Confirmada', completed: 'Completada', cancelled: 'Cancelada', no_show: 'No asistió' };
                        var modalityIcons = { presencial: User, videollamada: Video, telefonica: Phone };
                        var ModalityIcon = modalityIcons[apt.modality] || User;
                        
                        return (
                          <div key={apt.appointment_id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="font-semibold text-slate-50">{apt.patient_name}</span>
                                  <span className={'px-2 py-1 text-xs font-medium rounded-full border ' + (statusColors[apt.status] || statusColors.pending)}>{statusLabels[apt.status] || apt.status}</span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{apt.date}</span>
                                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{apt.time_slot}</span>
                                  <span className="flex items-center gap-1"><ModalityIcon className="w-4 h-4" />{apt.modality}</span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-2">
                                  <a href={'mailto:' + apt.patient_email} className="hover:text-emerald-400">{apt.patient_email}</a>
                                  <a href={'https://wa.me/' + apt.patient_phone.replace(/[^0-9]/g, '')} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 flex items-center gap-1"><Phone className="w-3 h-3" />{apt.patient_phone}</a>
                                </div>
                                {apt.reason && <p className="text-sm text-slate-400 mt-2 italic">"{apt.reason}"</p>}
                              </div>
                              <div className="flex gap-2">
                                {apt.status === 'pending' && (
                                  <>
                                    <Button size="sm" onClick={function() { updateAppointmentStatus(apt.appointment_id, 'confirmed'); }} className="bg-blue-500 hover:bg-blue-600"><CheckCircle className="w-4 h-4 mr-1" />Confirmar</Button>
                                    <Button size="sm" variant="outline" onClick={function() { updateAppointmentStatus(apt.appointment_id, 'cancelled'); }} className="border-red-500/50 text-red-400 hover:bg-red-500/10"><XCircle className="w-4 h-4 mr-1" />Cancelar</Button>
                                  </>
                                )}
                                {apt.status === 'confirmed' && (
                                  <>
                                    <Button size="sm" onClick={function() { updateAppointmentStatus(apt.appointment_id, 'completed'); }} className="bg-emerald-500 hover:bg-emerald-600"><CheckCircle className="w-4 h-4 mr-1" />Completar</Button>
                                    <Button size="sm" variant="outline" onClick={function() { updateAppointmentStatus(apt.appointment_id, 'no_show'); }} className="border-slate-600 text-slate-400 hover:bg-slate-700">No asistió</Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'analytics' && (
            <ProfileAnalyticsDashboard 
              analytics={analytics} 
              onExport={function() { window.open(API + '/professional/analytics/export?days=30', '_blank'); }} 
              entityType="professional" 
            />
          )}

          {activeTab === 'profile' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader><CardTitle className="text-slate-50">Información profesional</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Tipo de profesional</Label>
                        <Select value={formData.professional_type || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { professional_type: v }); }); }}>
                          <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">{professionalTypes.map(function(t) { return <SelectItem key={t.value} value={t.value} className="text-slate-300 focus:bg-slate-800">{t.label}</SelectItem>; })}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Número de licencia</Label>
                        <Input name="license_number" value={formData.license_number || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Especialización</Label>
                      <Input name="specialization" value={formData.specialization || ''} onChange={handleChange} placeholder="Ej: Adicciones, TCC..." className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Institución</Label>
                      <Input name="institution" value={formData.institution || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-slate-300">Biografía</Label>
                        <AIDescriptionButton
                          entityType="professional"
                          name={formData.name}
                          context={{
                            especialización: formData.specialization,
                            tipo: formData.professional_type,
                            ciudad: formData.city,
                            institución: formData.institution
                          }}
                          onGenerated={function(desc) { setFormData(function(prev) { return Object.assign({}, prev, { bio: desc }); }); }}
                        />
                      </div>
                      <Textarea name="bio" value={formData.bio || ''} onChange={handleChange} rows={4} placeholder="Tu experiencia y enfoque..." className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader><CardTitle className="text-slate-50">Contacto</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Teléfono</Label>
                        <Input name="phone" value={formData.phone || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                      <div>
                        <Label className="text-slate-300">WhatsApp</Label>
                        <Input name="whatsapp" value={formData.whatsapp || ''} onChange={handleChange} placeholder="+56 9 1234 5678" className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Región</Label>
                        <Select value={formData.region || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { region: v }); }); }}>
                          <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar región" /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            {CHILEAN_REGIONS.map(function(r) { return <SelectItem key={r.value} value={r.value} className="text-slate-300 focus:bg-slate-800">{r.label}</SelectItem>; })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Ciudad</Label>
                        <Select value={formData.city || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { city: v }); }); }}>
                          <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            {(formData.region ? getCitiesByRegion(formData.region) : cities).map(function(c) { return <SelectItem key={c} value={c} className="text-slate-300 focus:bg-slate-800">{c}</SelectItem>; })}
                            <SelectItem value="otra" className="text-slate-300 focus:bg-slate-800">Otra ciudad</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Dirección</Label>
                        <Input name="address" value={formData.address || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email</Label>
                        <Input name="email" type="email" value={formData.email || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Sitio Web</Label>
                        <Input name="website" value={formData.website || ''} onChange={handleChange} placeholder="https://..." className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-slate-700">
                      <Label className="text-slate-300 mb-3 block">Redes Sociales</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-400 text-xs">Facebook</Label>
                          <Input name="facebook" value={formData.facebook || ''} onChange={handleChange} placeholder="facebook.com/..." className="bg-slate-950 border-slate-700 text-slate-200" />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">Instagram</Label>
                          <Input name="instagram" value={formData.instagram || ''} onChange={handleChange} placeholder="@usuario" className="bg-slate-950 border-slate-700 text-slate-200" />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">LinkedIn</Label>
                          <Input name="linkedin" value={formData.linkedin || ''} onChange={handleChange} placeholder="linkedin.com/in/..." className="bg-slate-950 border-slate-700 text-slate-200" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Services & Modality */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader><CardTitle className="text-slate-50">Servicios y Modalidad</CardTitle></CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label className="text-slate-300 mb-3 block">Modalidad de Atención</Label>
                      <div className="flex flex-wrap gap-2">
                        {modalityOptions.map(function(m) {
                          var selected = (formData.modality || []).includes(m.value);
                          return <Button key={m.value} type="button" variant={selected ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('modality', m.value); }} className={selected ? 'bg-purple-500 hover:bg-purple-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{m.label}</Button>;
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-3 block">Servicios que ofreces</Label>
                      <div className="flex flex-wrap gap-2">
                        {serviceOptions.map(function(s) {
                          var selected = (formData.services || []).includes(s);
                          return <Button key={s} type="button" variant={selected ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('services', s); }} className={selected ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{s}</Button>;
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-3 block">Enfoques Terapéuticos</Label>
                      <div className="flex flex-wrap gap-2">
                        {approachOptions.map(function(a) {
                          var selected = (formData.approaches || []).includes(a);
                          return <Button key={a} type="button" variant={selected ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('approaches', a); }} className={selected ? 'bg-blue-500 hover:bg-blue-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{a}</Button>;
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pricing & Schedule */}
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader><CardTitle className="text-slate-50">Precios y Horarios</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Precio Primera Consulta (CLP)</Label>
                        <Input name="consultation_price" type="number" value={formData.consultation_price || ''} onChange={handleChange} placeholder="40000" className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Precio Sesión Regular (CLP)</Label>
                        <Input name="session_price" type="number" value={formData.session_price || ''} onChange={handleChange} placeholder="35000" className="bg-slate-950 border-slate-700 text-slate-200" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Horario de Atención</Label>
                      <Input name="schedule" value={formData.schedule || ''} onChange={handleChange} placeholder="Lun-Vie 9:00-18:00, Sáb 9:00-13:00" className="bg-slate-950 border-slate-700 text-slate-200" />
                      <p className="text-xs text-slate-500 mt-1">Describe tus horarios de atención</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-6">
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-50">Mostrar en directorio</p>
                        <p className="text-sm text-slate-400">Visible en búsquedas</p>
                      </div>
                      <Switch checked={formData.is_public || false} onCheckedChange={function(c) { setFormData(function(prev) { return Object.assign({}, prev, { is_public: c }); }); }} />
                    </div>
                  </CardContent>
                </Card>
                <Button onClick={handleSave} className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg" disabled={saving}>
                  {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar</>}
                </Button>
                
                {/* Preview */}
                <ProfessionalPreview data={formData} />
              </div>
            </div>
          )}

          {activeTab === 'photo' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="text-slate-50">Foto de perfil</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-6">
                  {formData.profile_image || auth.user?.picture ? (
                    <img src={formData.profile_image || auth.user?.picture} alt="Perfil" className="w-40 h-40 rounded-2xl object-cover ring-4 ring-slate-800" />
                  ) : (
                    <div className="w-40 h-40 rounded-2xl bg-slate-800 flex items-center justify-center">
                      <Brain className="w-16 h-16 text-slate-600" />
                    </div>
                  )}
                  <div className="text-center">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" ref={fileInputRef} />
                    <Button variant="outline" onClick={function() { fileInputRef.current?.click(); }} className="border-slate-700 text-slate-300 hover:bg-slate-800" disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Subir nueva foto
                    </Button>
                    <p className="text-sm text-slate-500 mt-2">JPG, PNG o WebP. Máx 5MB</p>
                  </div>
                </div>
                <div className="mt-6">
                  <Button onClick={handleSave} className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg" disabled={saving}>
                    {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar cambios</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'patients' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Users className="w-5 h-5 text-emerald-400" />Pacientes vinculados</CardTitle></CardHeader>
              <CardContent>
                {patients.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg text-slate-50 mb-2">Sin pacientes vinculados</h3>
                    <p className="text-slate-400 text-sm">Los pacientes que te seleccionen aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {patients.map(function(p) {
                      return (
                        <div key={p.user_id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><User className="w-5 h-5 text-emerald-400" /></div>
                            <div>
                              <p className="font-medium text-slate-200">{p.name || 'Paciente'}</p>
                              <p className="text-sm text-slate-400">{p.email}</p>
                            </div>
                          </div>
                          {p.addiction_type && <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm">{p.addiction_type}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'messages' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Mail className="w-5 h-5 text-emerald-400" />Mensajes de contacto</CardTitle></CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg text-slate-50 mb-2">No hay mensajes</h3>
                    <p className="text-slate-400 text-sm">Los mensajes de contacto aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map(function(msg) {
                      return (
                        <div key={msg.message_id} className="p-4 bg-slate-800/50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-200">{msg.sender_name}</span>
                            <span className="text-sm text-slate-500">{new Date(msg.created_at).toLocaleDateString('es-CL')}</span>
                          </div>
                          <p className="text-slate-400 text-sm mb-1">{msg.sender_email}</p>
                          {msg.sender_phone && <p className="text-slate-400 text-sm mb-2">{msg.sender_phone}</p>}
                          <p className="text-slate-300">{msg.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'alerts' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Bell className="w-5 h-5 text-emerald-400" />Alertas</CardTitle></CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg text-slate-50 mb-2">Sin alertas</h3>
                    <p className="text-slate-400 text-sm">Las alertas de pacientes aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {alerts.map(function(a, i) {
                      return (
                        <div key={i} className={'flex items-start gap-3 p-4 rounded-xl ' + (a.type === 'relapse' ? 'bg-red-500/10' : 'bg-yellow-500/10')}>
                          <AlertTriangle className={'w-5 h-5 flex-shrink-0 ' + (a.type === 'relapse' ? 'text-red-400' : 'text-yellow-400')} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-200">{a.user_name}</p>
                              <span className="text-xs text-slate-500">{a.date && new Date(a.date).toLocaleDateString('es-CL')}</span>
                            </div>
                            <p className="text-sm text-slate-400">{a.message}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'quotes' && (
            <QuoteRequestsInbox role="professional" />
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Pricing Settings */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-400" />
                    Configuración de Precios
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-50">Aceptar pagos online</p>
                      <p className="text-sm text-slate-400">Los pacientes podrán pagar la reserva al agendar</p>
                    </div>
                    <Switch
                      checked={pricingData.accepts_online_payment}
                      onCheckedChange={function(checked) {
                        setPricingData(function(prev) { return Object.assign({}, prev, { accepts_online_payment: checked }); });
                      }}
                    />
                  </div>
                  
                  {pricingData.accepts_online_payment && (
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-slate-200">Precio de Reserva (CLP)</Label>
                        <p className="text-xs text-slate-500 mb-2">Lo que el paciente paga para confirmar la cita</p>
                        <Input
                          type="number"
                          value={pricingData.reservation_price || ''}
                          onChange={function(e) {
                            setPricingData(function(prev) { return Object.assign({}, prev, { reservation_price: parseInt(e.target.value) || 0 }); });
                          }}
                          className="bg-slate-950 border-slate-700 text-slate-200"
                          placeholder="15000"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-200">Precio Total Consulta (CLP)</Label>
                        <p className="text-xs text-slate-500 mb-2">Precio completo de la consulta (opcional)</p>
                        <Input
                          type="number"
                          value={pricingData.consultation_price || ''}
                          onChange={function(e) {
                            setPricingData(function(prev) { return Object.assign({}, prev, { consultation_price: parseInt(e.target.value) || null }); });
                          }}
                          className="bg-slate-950 border-slate-700 text-slate-200"
                          placeholder="50000"
                        />
                      </div>
                    </div>
                  )}
                  
                  {pricingData.accepts_online_payment && (
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <p className="text-sm text-emerald-400">
                        <strong>Comisión de la plataforma:</strong> 35% del precio de reserva
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Por una reserva de ${(pricingData.reservation_price || 15000).toLocaleString('es-CL')} CLP, recibirás ${Math.round((pricingData.reservation_price || 15000) * 0.65).toLocaleString('es-CL')} CLP
                      </p>
                    </div>
                  )}
                  
                  <Button
                    onClick={async function() {
                      setSavingPricing(true);
                      try {
                        await authAxios.put(API + '/professional/pricing', pricingData, { withCredentials: true });
                        toast.success('Precios actualizados');
                      } catch (e) {
                        toast.error('Error al guardar');
                      }
                      setSavingPricing(false);
                    }}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    disabled={savingPricing}
                  >
                    {savingPricing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar Precios
                  </Button>
                </CardContent>
              </Card>

              {/* Google Calendar Integration */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    Google Calendar
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {calendarConnected ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-emerald-400" />
                        <div className="flex-1">
                          <p className="font-medium text-emerald-400">Conectado</p>
                          <p className="text-sm text-slate-400">Las citas confirmadas se agregarán automáticamente a tu Google Calendar</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={async function() {
                          try {
                            await authAxios.delete(API + '/professional/google-calendar/disconnect', { withCredentials: true });
                            setCalendarConnected(false);
                            toast.success('Google Calendar desconectado');
                          } catch (e) {
                            toast.error('Error al desconectar');
                          }
                        }}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Unlink className="w-4 h-4 mr-2" />
                        Desconectar Google Calendar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-slate-300 mb-2">Conecta tu Google Calendar para:</p>
                        <ul className="text-sm text-slate-400 space-y-1">
                          <li>• Ver tus citas automáticamente en tu calendario</li>
                          <li>• Recibir recordatorios de Google</li>
                          <li>• Evitar conflictos de horarios</li>
                        </ul>
                      </div>
                      <Button
                        onClick={async function() {
                          try {
                            const res = await authAxios.get(API + '/professional/google-calendar/auth', { withCredentials: true });
                            window.location.href = res.data.authorization_url;
                          } catch (e) {
                            toast.error('Error al conectar. Verifica que Google Calendar esté configurado.');
                          }
                        }}
                        className="w-full bg-blue-500 hover:bg-blue-600"
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Conectar Google Calendar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'advertising' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-10 h-10 text-amber-400" />
                </div>
                <h2 className="text-3xl font-bold text-slate-50 mb-2">Aumenta tu Visibilidad</h2>
                <p className="text-slate-400">Elige un plan y recibe más pacientes</p>
              </div>

              {/* Plans Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                
                {/* Plan Gratuito */}
                <Card className="bg-slate-900 border-slate-700">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-slate-300 mb-1">Gratuito</h3>
                    <p className="text-xs text-slate-500 mb-3">Presencia básica</p>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-slate-400">$0</span>
                      <span className="text-slate-500 text-sm"> / mes</span>
                    </div>
                    <ul className="space-y-1.5 mb-4 text-xs">
                      <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Perfil profesional</li>
                      <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Recibir reseñas</li>
                      <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin posición prioritaria</li>
                      <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin badge destacado</li>
                    </ul>
                    <Button variant="outline" disabled className="w-full border-slate-700 text-slate-500 text-sm">
                      Plan Actual
                    </Button>
                  </CardContent>
                </Card>

                {/* Plan Destacado */}
                <Card className="bg-gradient-to-br from-blue-500/10 via-blue-600/5 to-slate-900 border-blue-500/30">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-blue-400 mb-1">Destacado</h3>
                    <p className="text-xs text-slate-400 mb-3">Top 11-20</p>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-slate-50">$29.000</span>
                      <span className="text-slate-400 text-sm"> / mes</span>
                    </div>
                    <ul className="space-y-1.5 mb-4 text-xs">
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Badge "Destacado"</li>
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Posición Top 20</li>
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Estadísticas de perfil</li>
                      <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin prioridad chatbot</li>
                    </ul>
                    <Button
                      onClick={async function() {
                        try {
                          const res = await authAxios.post(API + '/professional/premium/checkout', {
                            professional_id: profile.professional_id,
                            origin_url: window.location.origin,
                            plan_type: 'destacado'
                          }, { withCredentials: true });
                          if (res.data.url && res.data.token) {
                            var form = document.createElement('form');
                            form.method = 'POST';
                            form.action = res.data.url;
                            var input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = 'token_ws';
                            input.value = res.data.token;
                            form.appendChild(input);
                            document.body.appendChild(form);
                            form.submit();
                          } else if (res.data.checkout_url) {
                            window.location.href = res.data.checkout_url;
                          }
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Error al procesar el pago');
                        }
                      }}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-sm"
                      data-testid="checkout-prof-destacado"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Elegir Plan
                    </Button>
                  </CardContent>
                </Card>

                {/* Plan Premium */}
                <Card className="bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-slate-900 border-amber-500/30 relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold text-amber-400 mb-1">Premium</h3>
                    <p className="text-xs text-slate-400 mb-3">Top 4-10</p>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-slate-50">$49.000</span>
                      <span className="text-slate-400 text-sm"> / mes</span>
                    </div>
                    <ul className="space-y-1.5 mb-4 text-xs">
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Badge "Premium"</li>
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Posición Top 10</li>
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Prioridad en TerapeutAI</li>
                      <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Soporte prioritario</li>
                    </ul>
                    <Button
                      onClick={async function() {
                        try {
                          const res = await authAxios.post(API + '/professional/premium/checkout', {
                            professional_id: profile.professional_id,
                            origin_url: window.location.origin,
                            plan_type: 'premium'
                          }, { withCredentials: true });
                          if (res.data.url && res.data.token) {
                            var form = document.createElement('form');
                            form.method = 'POST';
                            form.action = res.data.url;
                            var input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = 'token_ws';
                            input.value = res.data.token;
                            form.appendChild(input);
                            document.body.appendChild(form);
                            form.submit();
                          } else if (res.data.checkout_url) {
                            window.location.href = res.data.checkout_url;
                          }
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Error al procesar el pago');
                        }
                      }}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-sm"
                      data-testid="checkout-prof-premium"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Elegir Plan
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Plan por Referidos - OCULTO TEMPORALMENTE */}
              {false && (
              <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Target className="w-8 h-8 text-emerald-400" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-xl font-bold text-slate-50 mb-1">Plan por Referidos</h3>
                      <p className="text-slate-300 text-sm mb-2">
                        <strong className="text-emerald-400">Sin costo inicial.</strong> Solo pagas el 40% de tu primera consulta cuando un paciente llega a través de nuestra plataforma.
                      </p>
                      <p className="text-xs text-slate-500">Ejemplo: Si tu consulta es $50.000, pagarías $20.000 por cada paciente nuevo.</p>
                    </div>
                    <a href="/contacto?asunto=plan-referidos-profesional" className="flex-shrink-0">
                      <Button className="bg-emerald-500 hover:bg-emerald-600">
                        <Phone className="w-4 h-4 mr-2" />
                        Solicitar Plan
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ProfessionalAssistant />
    </div>
  );
}
